import React, { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../Context/AppContext";
import { loadAttributeValues, loadAttributes } from "api/Attributes";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import AuthManager from "Common/AuthManager";
import { TAttribute, TAttributeValue, TEditParent } from "types/Pages/ProductDelta";
import { TFilters } from "types/hooks/useTable";


export type TcategoryData = {
  category: { id: string, name: string }[]
  name: string
}

export type ProductsContextType = {
  selected_Category: { id?: string, name: string, deleted: boolean },
  setSelected_Category: React.Dispatch<React.SetStateAction<{
    id?: string | undefined;
    name: string;
    deleted: boolean;
  }>>,
  attributes: TAttribute[] | null,
  attributeError: boolean,
  categoryData: TcategoryData[],
  setCategoryData: React.Dispatch<React.SetStateAction<TcategoryData[]>>,
  showLoader: boolean,
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>,
  getCategoryTypeAndCategory: () => Promise<void>,
  editParent: TEditParent | null,
  setEditParent: React.Dispatch<React.SetStateAction<TEditParent | null>>,
  removeChildModal: boolean,
  setRemoveChildModal: React.Dispatch<React.SetStateAction<boolean>>,
  deleteParent: TEditParent | null,
  setDeleteParent: React.Dispatch<React.SetStateAction<TEditParent | null>>,
  pageSize: number | null,
  setPageSize: React.Dispatch<React.SetStateAction<number | null>>,
  fetchAttributes: () => Promise<void>,
  setAttributePage: React.Dispatch<React.SetStateAction<number>>,
  totalAttributeCount: number,
  attributePage: number,
  setTotalAttributeCount: React.Dispatch<React.SetStateAction<number>>,
  setAttributes: React.Dispatch<React.SetStateAction<TAttribute[] | null>>
  resetAttributeData: () => void,
  setCategorySearch: React.Dispatch<React.SetStateAction<string>>
  handleCategorySearchChange: (query: string) => void,
  fetchAttributeValues: (attributeId: string, page: number, searchQuery?: string, existingFilters?: string[]) => void
  selectedFilterList: React.MutableRefObject<TAttribute[]>
  toggleAttributeValueInPlace: (attributes: TAttribute[], attributeId: string, attributeName: string, attributeValueId: string, attributeValueName: string) => TAttribute[]
  loadingAttributes: boolean
  filters: TFilters | null
  setFilters: React.Dispatch<React.SetStateAction<TFilters | null>>
  findSelectedCategoryTypeName: (categoryData: any[], categoryId: string) => string
}

export const ProductsContext = createContext<ProductsContextType>({} as ProductsContextType);

export const ProductsContextProvider = ({ children }: PropsWithChildren) => {
  // dont delete this
  const [attributes, setAttributes] = useState<TAttribute[] | null>(null);
  const [attributeError, setAttributeError] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [editParent, setEditParent] = useState<TEditParent | null>(null)
  const [removeChildModal, setRemoveChildModal] = useState<boolean>(false);
  const [deleteParent, setDeleteParent] = useState<TEditParent | null>(null);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [attributePage, setAttributePage] = useState<number>(1);
  const [totalAttributeCount, setTotalAttributeCount] = useState<number>(0);
  const [loadingAttributes, setLoadingAttributes] = useState<boolean>(false);
  const [categorySearch, setCategorySearch] = useState<string>("")
  const selectedFilterList = useRef<TAttribute[]>([]);
  const [filters, setFilters] = useState<TFilters | null>(pageSize ? { limit: pageSize } : null)
  const attributeLimit = 10;

  const appData = useContext(AppContext);
  const industryId = appData?.get_selected_industry()?.id;
  const apiBaseUrl = AppSettings.GMS_BACKEND_API_URL;

  let [selected_Category, setSelected_Category] = React.useState<{ id?: string, name: string, deleted: boolean }>({
    id: undefined,
    name: "",
    deleted: false,
  });

  const [categoryData, setCategoryData] = useState<TcategoryData[]>([]);

  useEffect(() => {
    if (!industryId) return;
    if (categoryData?.length === 0) {
      getCategoryTypeAndCategory();
    } else {
      setShowLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryId]);

  useEffect(() => {
    fetchAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected_Category, attributePage, categorySearch]);

  const resetAttributeData = (() => {
    selectedFilterList.current = []
    setFilters((prev) => { return { ...prev, filters: [] } })
    setCategorySearch("")
    setAttributes([])
    setAttributePage(1)
    setTotalAttributeCount(0)
  })

  const handleCategorySearchChange = ((query: string) => {
    setAttributes([])
    setAttributePage(1)
    setTotalAttributeCount(0)
    setCategorySearch((prev) => prev !== query ? query : '');
  });

  async function getCategoryTypeAndCategory() {
    if (!industryId) return;
    setShowLoader(true);
    let res = await ServiceWrapper.doGet(
      apiBaseUrl + `industry/${industryId}/categorytype/category`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (res?.data?.done) {
      setShowLoader(false);
      setCategoryData(res?.data?.data);
    } else {
      setShowLoader(false);
    }
  }

  const findSelectedCategoryTypeName = (categoryData: any[], categoryId: string): string => {
    for (const categoryType of categoryData) {
      const foundCategory = categoryType.category.find(
        (category: any) => category.id === categoryId
      );

      if (foundCategory) {
        return categoryType.name;
      }
    }

    return "";
  };

  const fetchAttributes = async () => {
    if (!industryId || !selected_Category.id) return;
    setLoadingAttributes(true);

    const attributeResponse = await loadAttributes([selected_Category.id], attributePage, attributeLimit, categorySearch);
    const newAttributes = attributeResponse[1].data[0]?.attributes ?? [];

    setTotalAttributeCount(attributeResponse[1].data[0]?.total_count);
    setAttributeError(attributeResponse[0]);

    setAttributes((prev) =>
      attributePage === 1
        ? newAttributes.map((attribute: TAttribute) => ({
          ...attribute,
          values: attribute.values || [],
        }))
        : [...prev ?? [], ...newAttributes.map((attribute: TAttribute) => ({
          ...attribute,
          values: attribute.values || [],
        }))]
    );
    setLoadingAttributes(false);
  };

  const fetchAttributeValues = async (attributeId: string, page: number, searchQuery: string = '', existingFilters?: string[]) => {

    if (!attributeId) return;

    const attributeValuesResponse = await loadAttributeValues([attributeId], page, attributeLimit, searchQuery);
    const newAttributeValues = attributeValuesResponse[1].data[0]?.attribute_values ?? [];
    const attributeValueCount = attributeValuesResponse[1].data[0]?.attributeValueCount ?? 0;

    setAttributes((prevAttributes) => {
      if (!prevAttributes) return null;

      return prevAttributes.map((attribute) => {
        if (attributeId === attribute.id) {
          // Combine new values with existing ones
          let newValues: TAttributeValue[] = page === 1 ? newAttributeValues : [...(attribute.values ?? []), ...newAttributeValues];

          if (existingFilters && existingFilters.length > 0) {
            const existingFilterSet = new Set(existingFilters);

            // Get the filtered values from existing ones that match existingFilters
            const filteredValues = (attribute.values ?? []).filter(
              (value) => existingFilterSet.has(value.id)
            );

            // Combine filtered values with new values
            newValues = [...filteredValues, ...newValues];
          }

          // Deduplicate the combined list based on unique IDs
          const uniqueValueSet = new Set();
          const uniqueValues: TAttributeValue[] = [];

          for (const value of newValues) {
            if (!uniqueValueSet.has(value.id)) {
              uniqueValueSet.add(value.id);
              uniqueValues.push(value);
            }
          }

          return {
            ...attribute,
            values: uniqueValues,
            attributeValueCount: attributeValueCount,
          };
        } else {
          return attribute;
        }
      });
    });

  };

  const toggleAttributeValueInPlace = (
    attributes: TAttribute[],
    attributeId: string,
    attributeName: string,
    attributeValueId: string,
    attributeValueName: string
  ): TAttribute[] => {
    // Find the target attribute
    let attributeIndex = attributes.findIndex((attr) => attr.id === attributeId);

    if (attributeIndex === -1) {
      // If the attribute doesn't exist, create a new one
      const newAttribute: TAttribute = {
        id: attributeId,
        name: attributeName, // Use the provided name
        variant_defining: false, // Default value, change as needed
        values: [{ id: attributeValueId, value: attributeValueName }], // Adding initial value
        attributeValueCount: 1,
      };

      attributes.push(newAttribute);
      return attributes;
    }

    const attribute = attributes[attributeIndex];
    const valueIndex = attribute.values.findIndex(
      (value) => value.id === attributeValueId
    );

    if (valueIndex >= 0) {
      // If the value exists, remove it
      attribute.values.splice(valueIndex, 1);

      // If the attribute becomes empty, remove it from the list
      if (attribute.values.length === 0) {
        attributes.splice(attributeIndex, 1);
      }
    } else {
      // If the value doesn't exist, add it
      attribute.values.push({
        id: attributeValueId,
        value: attributeValueName,
      });
      attribute.attributeValueCount = attribute.values.length;
    }

    return attributes;
  };



  return (
    <ProductsContext.Provider
      value={{
        selected_Category,
        setSelected_Category,
        attributes,
        attributeError,
        categoryData,
        setCategoryData,
        showLoader,
        setShowLoader,
        getCategoryTypeAndCategory,
        editParent,
        setEditParent,
        removeChildModal,
        setRemoveChildModal,
        deleteParent,
        setDeleteParent,
        fetchAttributes,
        pageSize,
        setPageSize,
        setAttributePage,
        totalAttributeCount,
        attributePage,
        setTotalAttributeCount,
        setAttributes,
        resetAttributeData,
        setCategorySearch,
        handleCategorySearchChange,
        fetchAttributeValues,
        selectedFilterList,
        toggleAttributeValueInPlace,
        loadingAttributes,
        filters,
        setFilters,
        findSelectedCategoryTypeName
      }}>
      {children}
    </ProductsContext.Provider>
  );
};
